import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from './Toast';
import { signIn, resetPassword } from '../lib/auth';
import { SHOW_TEST_ACCOUNTS, TEST_ACCOUNTS } from '../lib/testAccounts';

export default function Login({ onLogin }) {
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // Deep-link from the reset/invite pages: /login?forgot=1 opens the reset form.
  const [mode, setMode] = useState(searchParams.get('forgot') ? 'forgot' : 'login'); // 'login' | 'forgot'

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      // Validation: keep local state, but surface via toast.
      const msg = 'Please enter your email and password.';
      showToast(msg, { kind: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      await signIn({ email, password });
      onLogin();
    } catch (err) {
      // A 401 means the credentials were genuinely wrong — point the user at the
      // reset flow. Anything else is a transient/server problem, so don't blame
      // their password.
      const msg = err?.status === 401
        ? 'Login failed. Please use “Forgot your password?” below to reset your password.'
        : 'Couldn’t sign in right now. Please try again in a moment.';
      showToast(msg, { kind: 'error' });
      console.error('Login error:', err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    if (!email) {
      const msg = 'Please enter your email address to reset your password.';
      showToast(msg, { kind: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      const msg = 'Failed to send reset password email. Please try again.';
      showToast(msg, { kind: 'error' });
      console.error('Reset password error:', err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[600px] flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h2 className="text-NBNA-primary">Sign in to your account</h2>

          {/* Test credentials note */}
          {import.meta.env.VITE_USE_MOCK_AUTH === 'true' && (
            <div className="mt-2 text-sm text-gray-600 space-y-1">
              <p className="font-medium">Test Credentials (mock auth):</p>
              <p>Admin: admin@NBNA.com / admin123</p>
              <p>Prospect: prospect@NBNA.com / prospect123</p>
              <p>Member: member@NBNA.com / member123</p>
            </div>
          )}

          {SHOW_TEST_ACCOUNTS && (
            <div className="mt-4 text-sm text-gray-600 space-y-1">
              <p className="font-medium">Test Accounts (live auth):</p>
              {TEST_ACCOUNTS.map((account) => (
                <p key={account.email}>
                  {account.label}: {account.email} / {account.password}
                </p>
              ))}
            </div>
          )}
        </div>

        {resetSent ? (
          <div className="bg-green-50 text-green-700 p-4 rounded-md text-sm">
            Check your email — a reset link was sent.
          </div>
        ) : (
          <form onSubmit={mode === 'login' ? handleLogin : handleSendReset} className="space-y-6">

            <div>
              <label htmlFor="email" className="block text-sm font-lato text-NBNA-primary">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field mt-1 block w-full"
                placeholder="your@email.com"
              />
            </div>

            {mode === 'login' && (
              <div>
                <label htmlFor="password" className="block text-sm font-lato text-NBNA-primary">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field mt-1 block w-full"
                  placeholder="••••••••"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              {mode === 'login' ? (
                <button
                  type="button"
                  onClick={() => {
                    setMode('forgot');
                  }}
                  className="text-sm text-NBNA-primary hover:text-NBNA-primary/70 transition-colors"
                >
                  Forgot your password?
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                  }}
                  className="text-sm text-NBNA-primary hover:text-NBNA-primary/70 transition-colors"
                >
                  Back to login
                </button>
              )}
            </div>

            {mode === 'login' ? (
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex justify-center items-center"
              >
                {isLoading ? 'Signing in…' : 'Sign in'}
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex justify-center items-center"
              >
                {isLoading ? 'Sending…' : 'Send Reset'}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
